import { NgClass, NgTemplateOutlet } from '@angular/common'
import {
  AfterViewInit, Component, ContentChild, ContentChildren, EventEmitter, inject, Input, LOCALE_ID, OnChanges,
  OnDestroy, OnInit, Output, QueryList, SimpleChanges, TemplateRef, ViewChild,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router'
import { Observable, Subscription } from 'rxjs'

import { FilterMetadata, SelectItem, SelectItemGroup, PrimeTemplate } from 'primeng/api'
import { ButtonDirective } from 'primeng/button'
import { Checkbox } from 'primeng/checkbox'
import { PrimeNG } from 'primeng/config'
import { DatePicker } from 'primeng/datepicker'
import { InputNumber } from 'primeng/inputnumber'
import { InputText } from 'primeng/inputtext'
import { MultiSelect } from 'primeng/multiselect'
import { Ripple } from 'primeng/ripple'
import { Select } from 'primeng/select'
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table'

import { Tooltip } from 'primeng/tooltip'
import { cloneDeep, forEach, isBoolean, remove } from 'lodash-es'

import { TitleService } from '@app/services/title.service'
import { UserService } from '@app/services/user.service'
import { UtilitiesService } from '@app/services/utilities.service'
import { ConfigureFiltersService } from '@app/components/configure-filters/configure-filters.service'
import { FilterServicePayload } from '@app/components/configure-filters/filter'
import { FilterType } from '@app/components/configure-filters/filter.constants'
import { GlobalSearchService } from '@app/components/global-search/global-search.service'
import { TourKey } from '@app/components/tour/tour.constants'
import { TourService } from '@app/components/tour/tour.service'

import { Localizable, PagedResponse } from './generic'
import { GenericColumnComponent } from './generic-column'
import {
  asArray, buildFilterKeyValue, getVisibleColumns, identity, lazyLoadEventToUrlParams, Params, urlParamsToLazyLoadEvent,
} from './utilities'

/**
 * Wrapper for PrimeNG `p-table` providing a simplified interface to table / columns configuration.
 *
 * Example usage:
 *
   <app-generic-list [onLazyLoad]="load" [api]="api">

        <app-generic-column key="code_acted" title="Code Acted">
            <ng-template let-item>
                <a [routerLink]="['/', 'countries', 'detail', item.code_acted]">{{ identity(item).code_acted }}</a>
            </ng-template>
        </app-generic-column>
        <app-generic-column key="code_iso" title="Code ISO"></app-generic-column>

    </app-generic-list>
 *
 *
 */
@Component({
  selector: 'app-generic-list',
  templateUrl: 'generic-list.html',
  styleUrl: 'generic-list.css',
  host: {
    style: 'display: contents',
  },
  imports: [
    TableModule,
    PrimeTemplate,
    Tooltip,
    NgClass,
    Select,
    FormsModule,
    NgTemplateOutlet,
    MultiSelect,
    Checkbox,
    DatePicker,
    InputNumber,
    InputText,
    ButtonDirective,
    Ripple,
  ],
})
export class GenericListComponent<T> implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  public readonly router = inject(Router)
  public readonly activatedRoute = inject(ActivatedRoute)
  public readonly userService = inject(UserService)
  public readonly titleService = inject(TitleService)
  public readonly utilitiesService = inject(UtilitiesService)
  public readonly globalSearchService = inject(GlobalSearchService)
  public readonly configureFiltersService = inject(ConfigureFiltersService)
  public readonly locale = inject(LOCALE_ID)
  public readonly tourService = inject(TourService)
  public readonly primeng = inject(PrimeNG)

  // data to be rendered, normally populated by the load() function called when onLazyLoad() is emitted
  @Input() tableData!: T[]
  // name of data identifier, used for row expansion
  @Input() dataKey: string = 'id'
  // name of the key containing the datafragment for advanced routing (e.g. project detail)
  @Input() dataFragmentKey?: keyof T
  // default sort field
  @Input() multiSortMeta: any = []
  @Input() styleHeight: string = ''
  @Input() showPaginator: boolean = true
  @Input() enableSort: boolean = true
  @Input() rowExpandMode: 'none' | 'single' | 'multiple' = 'none'
  @Input() expandedRowKeys: { [s: string]: boolean } = {}
  // class to apply to the 'new row' that is created when a row is expanded, i.e. the 'content' of the expanded row
  @Input() expandedRowContentClass: string = ''
  // styleClass attribute to pass through to PrimeNG
  @Input() styleClass: string = ''
  // optionally group rows by field
  @Input() groupRowsBy?: string = undefined

  // will contain the optional row group template
  @ContentChild('RowGroupTemplate') rowGroupTemplateRef: TemplateRef<any> | null = null

  @Input() titleOverride?: string

  // function called by underlying p-table to receive data with new filter/sort conditions
  @Input() onLazyLoad!: CallableFunction
  // boolean controlling loading mask for table
  loading: boolean = false
  @Output() dataLoaded: EventEmitter<void> = new EventEmitter()
  // the method on the api to call for the load
  @Input() apiLoadMethod: string = 'list'
  // function to call to return a css class for the data row
  @Input() rowClassCallable: CallableFunction = (_item: any, _expanded: boolean) => {}
  // control selection mode of row(s)
  @Input() selectionMode: 'single' | 'multiple' | null | undefined = undefined
  @Input() selectedItems: T|T[] = []
  @Output() selectedItemsChange: EventEmitter<T[]> = new EventEmitter()
  @Input() checkboxSelection: boolean = false
  @Input() rowCanBeSelected = (_: T) => true
  // api function to call to retrieve data
  @Input() api!: any
  // In some cases we want to disable API calls (like in Tour).
  @Input() disableApi: boolean = false
  // observer for the signal to refresh the table
  @Input() tableReloadObservable?: Observable<void>
  // set certain table filters that the user is not able to remove
  @Input() forceFilters: { [key: string]: string|number|boolean|undefined } = {}
  // Whether to update the url bar for sort / filter activities.
  // For lists embarked in detail pages this may be interesting to set false
  @Input() updateUrlOnLoad: boolean = true
  // optional function to call on row click
  @Input() onRowClick?: CallableFunction

  // event fires whenever data changes, event contains new data
  @Output() dataChange: EventEmitter<PagedResponse<T>> = new EventEmitter()
  @Output() rowSelectChange: EventEmitter<any> = new EventEmitter()
  @Output() rowUnselectChange: EventEmitter<any> = new EventEmitter()

  // will contain the `generic-columns` list from the parent component
  @ContentChildren(GenericColumnComponent) columns!: QueryList<GenericColumnComponent>
  // list of fieldnames that should be shown, will be set to all columns names on load
  @Input() visibleColumnKeys: string[] = []
  // event called when all columns are loaded
  @Output() columnsLoad: EventEmitter<GenericColumnComponent[]> = new EventEmitter()
  public allColumns: GenericColumnComponent[] = []
  // provide a unique table key for manipulating this table via observer messages (e.g. filters)
  @Input({ required: true }) uniqueKey!: string
  // will contain the optional row expansion template
  @ContentChild('ExpansionTemplate') expansionTemplateRef!: TemplateRef<any>
  // reference to the underlying primeng table object
  @ViewChild(Table) pTable!: Table

  // total length of data (i.e. no ignoring pagination) corresponding to current filter conditions
  public totalRecords: number = 0

  // boolean flag indicating that the table params have been loaded from the url on initial page load
  private filterInited = false

  // calculated boolean flag indicating if a mobile vew should be displayed
  public showMobileView: boolean = false

  // this is used to track the most latest filter params, in order to trigger potentially-filtered reload
  public lastLoadEvent?: TableLazyLoadEvent
  // subscriptions declared in order to unsubscribe when we destroy the component
  public tableReloadSubscription?: Subscription
  public filtersChangedSubscription?: Subscription
  public routerSubscription?: Subscription
  public globalSearchValueSubscription?: Subscription
  public globalSearchTriggerSubscription?: Subscription
  public titleServiceSubscription?: Subscription
  private readonly resizeListener = () => this.checkScreenSize()

  // current value of the global search box, updated via a subscription
  public globalSearchText: string = ''

  // This boolean is used to disable the selectable-rows flag on the row. This is necessary because we pass [pSelectableRow]
  // to the row in the view, and even passing null or undefined or false is enough for prime to add the css class, which triggers
  // the pointer and background css.
  // this boolean will be flipped automatically if the above `onRowClick` is altered by the client component
  @Input() public hasSelectableRows: boolean = false

  // this should be overridden by the parent component
  public detailCreateUrl: string = '' // e.g. '/countries/detail'
  @Input() prefixDetailUrlTemplate: string = '' // e.g. '/countries/detail' (used for redirecting to a specific item when clicking on a row)

  // The name of the field to target to filter drop down lists / translatable objects.
  // This will be overridden if we detect the `fr` locale
  public filterLabelField: keyof Localizable = 'label_en'
  protected readonly isBoolean = isBoolean
  protected readonly identity = identity<T>
  protected readonly TourKey = TourKey
  protected readonly FilterType = FilterType

  constructor () {
    // used for drop-down filters
    if (this.locale === 'fr') {
      this.filterLabelField = 'label_fr'
    }
  }

  ngOnInit () {
    // Only run this code if we're being run in the context of the GenericListComponent, (i.e. not the child component extending it)
    if (Object.getPrototypeOf(this) === GenericListComponent.prototype) {
      // determine if there was a `onRowClick` function passed, and if so flip this to yes to trigger the selectable css on the row
      this.hasSelectableRows = this.onRowClick !== undefined

      // if we are passed an observable to be used to trigger table refreshes then subscribe to it now
      if (this.tableReloadObservable) {
        this.tableReloadSubscription = this.tableReloadObservable.subscribe(() => this.reload())
      }

      // Setup the GlobalSearchService value subscription, which will ensure we have the correct value from the url
      // from the very beginning of the component lifecycle
      this.globalSearchValueSubscription = this.globalSearchService.getValue().subscribe(valueMap => {
        for (const [key, value] of valueMap) {
          if (key === this.uniqueKey) {
            this.globalSearchText = value || ''
          }
        }
      })

      // Setup the GlobalSearchService trigger subscription, to trigger search as required.
      this.globalSearchTriggerSubscription = this.globalSearchService.getTrigger().subscribe(() => {
        // when the user searches using the global search, reset the sort to enable the elastic-search
        // relevance-based sort to work
        this.lastLoadEvent!.multiSortMeta = []
        this.pTable.multiSortMeta = []
        // this is required to visually render the sort in the table
        this.pTable.tableService.onSort(this.pTable.multiSortMeta)
        this.load(this.lastLoadEvent!)
      })
    } else {
      // only run if we are not the generic list component instance
      this.titleServiceSubscription = this.titleService.getUpdateTitle().subscribe(() => {
        if (this.updateUrlOnLoad) this.titleService.setPageTitle(this.returnTitle())
      })
    }

    window.addEventListener('resize', this.resizeListener)
  }

  /**
   * Called *once* when the table first loads.
   */
  ngAfterViewInit () {
    // only run this code if we're being run in the context of the GenericListComponent, (i.e. not a child component extending it)
    if (Object.getPrototypeOf(this) !== GenericListComponent.prototype) return

    this.filtersChangedSubscription = this.configureFiltersService.getFiltersChangedObservable().subscribe(filterPayloadMap => {
      if (!filterPayloadMap.payload) return
      // is it our table that changed ?
      if (filterPayloadMap.uniqueKey === this.uniqueKey) {
        this.changeActiveFilters(filterPayloadMap.payload)
      }
    })

    // Once the list of child GenericColumnComponents has been added we can populate our list of
    // visibleColumnKeys, and emit the event containing the list of all columns in case a consumer
    // is interested, e.g. to present a list of available columns for visibility control.
    setTimeout(() => {
      // apply the persisted visible columns, if they have been customised
      const persistedColumns = getVisibleColumns(this.uniqueKey)
      if (persistedColumns && persistedColumns.length > 0) {
        this.visibleColumnKeys = persistedColumns
      }

      if (this.visibleColumnKeys.length === 0) {
        this.columns.toArray().forEach(column => {
          this.visibleColumnKeys.push(column.key)
        })
      }
      // update its list of fields here
      this.columnsLoad.emit(this.columns.toArray())
      this.configureFiltersService.setColumns(this.uniqueKey, this.columns.toArray())
    })
  }

  ngOnChanges (changes: SimpleChanges) {
    // only run this code if we're being run in the context of the GenericListComponent, (i.e. not a child component extending it)
    if (Object.getPrototypeOf(this) !== GenericListComponent.prototype) return

    // Reload table when forced filters are changed.
    // This happens when the list view component is embedded in another component,
    // and the parent component changes.
    if (changes.forceFilters?.previousValue !== changes.forceFilters?.currentValue) {
      if (this.pTable) {
        this.resetTableStateFromMap(new Map<string, any>())
      }
    }
  }

  ngOnDestroy () {
    this.titleServiceSubscription?.unsubscribe()
    window.removeEventListener('resize', this.resizeListener)

    if (Object.getPrototypeOf(this) !== GenericListComponent.prototype) return
    this.tableReloadSubscription?.unsubscribe()
    this.routerSubscription?.unsubscribe()
    this.globalSearchValueSubscription?.unsubscribe()
    this.globalSearchTriggerSubscription?.unsubscribe()
    this.filtersChangedSubscription?.unsubscribe()
  }

  /**
   * Should be overridden by child lists.
   * Returning an empty string will not update the title.
   */
  returnTitle (): string {
    return ''
  }

  private checkScreenSize () {
    const isSmallScreen = window.innerWidth < 768
    const hasData = this.tableData?.length > 0
    const hasMobileColumns = this.columns?.filter(c => c.isMobileColumn).length > 0

    this.showMobileView = isSmallScreen && hasData && hasMobileColumns
  }

  /**
   * Return a css class if the given column key has a currently active filter, used as a temp fix for this bug:
   * https://github.com/primefaces/primeng/issues/16576
   * This should be deleted once that bug is fixed in PrimeNG
   */
  hasActiveFilter (columnKey: string): string {
    if (!this.lastLoadEvent?.filters) return ''
    if (Object.hasOwn(this.lastLoadEvent.filters, columnKey)) {
      let filter = this.lastLoadEvent.filters[columnKey]
      if (!filter) return ''
      if (!Array.isArray(filter)) {
        filter = [filter]
      }
      if (filter[0].value && filter[0].value !== 0) {
        return 'active-filter'
      }
    }
    return ''
  }

  /**
   * Update the underlying PrimeNG table from the incoming 'url parameters' map.
   *
   * This is used then either:
   * 1. the component initially loads, potentially with 'deep linked' filters / sort config; or
   * 2. the user uses the back / forward within the same component / angular route, and we need to reset the filter / sort config
   */
  resetTableStateFromMap (paramMap: Map<string, any>) {
    if (!this.pTable) return

    const newEvent = urlParamsToLazyLoadEvent(paramMap)

    // set the sort config on the table state, falling back to table default if nothing
    // is specified in the URL
    if (newEvent.multiSortMeta?.length) {
      this.pTable.multiSortMeta = newEvent.multiSortMeta || []
    } else {
      // if there was nothing in the url, then update the event with the default table config
      newEvent.multiSortMeta = this.pTable.multiSortMeta || undefined
    }

    // apply the filters and pagination to the table to ensure they persist after future user interactions
    setTimeout(() => {
      // update the paginator
      this.pTable.first = newEvent.first || 0
      this.pTable.rows = newEvent.rows || 100

      for (const key in this.pTable.filters) {
        if (key in this.forceFilters) continue
        forEach(this.pTable.filters[key], (filter: FilterMetadata) => {
          filter.value = null
        })
      }

      forEach(newEvent.filters, (filter: any, key: string) => {
        // Update the table filters config from the event filter.
        // This is unfortunately necessary to force the rendering of the filter correctly when loading the filter from the url
        this.pTable.filters[key] = filter
      })
      // this is required to visually render the sort in the table
      this.pTable.tableService.onSort(this.pTable.multiSortMeta || null)
    })
    // now the initial load is deblocked, call the initial load with url params taken into account
    this.filterInited = true
    this.load(newEvent)
  }

  /**
   * user adds a new instance of the listed class
   */
  onClickAdd () {
    this.router.navigate([this.detailCreateUrl])
  }

  rowClick ($event: MouseEvent, item: T) {
    const handled = this.handleClickOnTarget($event)
    if (handled) return

    // If user is doing a selection, then do nothing.
    // This should allow users to select for copy / paste without following the link.
    const selection = window.getSelection()?.toString()
    if (selection !== '') return

    // If the data key or the URL prefix is not set, then do nothing as we won't be able to redirect anywhere.
    const idField = this.dataKey as keyof typeof item
    if (!idField || !this.prefixDetailUrlTemplate) return

    let urlElements = this.prefixDetailUrlTemplate.split('/').filter(e => !!e)

    const urlCreationOptions: NavigationExtras = {}
    if (this.dataFragmentKey) {
      urlCreationOptions.fragment = item[this.dataFragmentKey] as string
    }

    // First detect if the control key is pressed, if so launch the new tab.
    if ($event.ctrlKey) {
      // If we have a ctrl-click then we need to build the url string, this is made slightly complicated by the locale, which is not visible to angular.
      // Start by assuming the dev case: no locale in url.
      urlElements.push(item[idField]!.toString())
      // If the current url starts with the locale, as will be the case for prod, prepend the locale in the new url.
      if (window.location.pathname.startsWith('/' + this.locale + '/')) {
        urlElements = ['/', this.locale].concat(urlElements)
      }

      window.open(this.router.createUrlTree(urlElements, urlCreationOptions).toString(), '_blank', 'noopener,noreferrer')
      return
    }

    // reset the global search to ensure the new page does not inherit its value
    this.globalSearchService.setValue({ uniqueKey: this.uniqueKey, value: '' })
    // Normal click… follow the link
    this.router.navigate(urlElements.concat([item[idField]!.toString()]), urlCreationOptions)
  }

  /**
   * When clicking on a table row:
   * - if it's on a link or button then do nothing and let the browser handle it
   * - if it's on a checkbox, tick the box
   * - if it's not on some text, do nothing as well
   */
  private handleClickOnTarget ($event: MouseEvent): boolean {
    let target = $event.target as any
    // If clicked on an SVG, try to use the current bubbled-up target
    if (target.closest('svg') && $event.currentTarget) target = $event.currentTarget

    // If content is a checkbox, click it.
    const checkbox = target.closest('p-checkbox-input') || target.querySelector('p-checkbox-input')
    if (checkbox) {
      checkbox.click($event)
      return true
    }

    // If content is a link, do nothing.
    if (target.closest('a') || target.querySelector('a')) return true

    // If content is a button, do nothing.
    if (target.closest('button') || target.querySelector('button')) return true

    // If user is not clicking on the cell content (the text), then do nothing.
    return !target.closest('.cell-content')
  }

  /**
   * add or clear a filter in the current genericList table
   */
  addFilter (fieldName: string, filter: FilterMetadata, performLoad = true) {
    if (!this.lastLoadEvent) return

    // start with the user's current filters
    const newEvent = cloneDeep(this.lastLoadEvent) // Use `cloneDeep` instead of `structuredClone` because TableLazyLoadEvent.forceUpdate is a function.
    // simulate the user adding the new filter on top
    newEvent.filters![fieldName] = filter
    // update the primeng table UI
    this.pTable.filters[fieldName] = filter
    // reset the pagination
    newEvent.first = 0
    // in case we are not reloading now, load the new filter / pagination into the 'last used' event
    // to ensure it is picked up on the next load
    this.lastLoadEvent = newEvent
    // trigger the reload
    if (performLoad) this.load(newEvent)
  }

  /**
   * remove a filter in the current genericList table
   */
  removeFilter (fieldName: string, performLoad = true) {
    if (!this.lastLoadEvent) return

    // start with the user's current filters
    const newEvent = cloneDeep(this.lastLoadEvent) // Use `cloneDeep` instead of `structuredClone` because TableLazyLoadEvent.forceUpdate is a function.
    // simulate the user adding the new filter on top
    delete newEvent.filters![fieldName]
    // update the primeng table UI
    delete this.pTable.filters[fieldName]
    // reset the pagination
    newEvent.first = 0
    // in case we are not reloading now, load the new filter / pagination into the 'last used' event
    // to ensure it is picked up on the next load
    this.lastLoadEvent = newEvent
    if (performLoad) this.load(newEvent)
  }

  reload () {
    if (this.lastLoadEvent) {
      this.load(this.lastLoadEvent)
    }
  }

  /**
   * Return a dict of the filters currently used in this list.
   *
   * Used for example to restrict exports to the same filter as the current view.
   */
  getFilterParams (): Params {
    let urlParams = lazyLoadEventToUrlParams(this.lastLoadEvent)
    // apply any compulsory filters we may have
    urlParams = Object.assign(urlParams, this.forceFilters)
    return urlParams
  }

  /**
   * Clear the non-forced filters for the table.
   *
   * Intended to be used like this from a list Component
   *
   *    <p-button icon="fa fa-solid fa-filter-circle-xmark" (onClick)="genericListComponent.clearFilters()"
            severity="secondary">
        </p-button>
   */
  clearFilters (performLoad = true, clearGlobalSearch = true) {
    if (!this.lastLoadEvent) return

    // reset the global search value
    if (clearGlobalSearch) this.globalSearchService.setValue({ uniqueKey: this.uniqueKey, value: '' })

    const newLoadEvent = cloneDeep(this.lastLoadEvent) // Use `cloneDeep` instead of `structuredClone` because TableLazyLoadEvent.forceUpdate is a function.
    for (const key in newLoadEvent.filters ?? {}) {
      // if the filter is included in the forceFilters, don't remove it
      if (key in this.forceFilters) continue

      const filters = asArray(newLoadEvent.filters![key])
      // loop over all the filters in the event and set them to null
      filters.forEach(filter => (filter.value = null))
      // update the underlying prime table as well to ensure the UI is aligned with the config
      this.pTable.filters[key] = filters
    }
    if (performLoad) {
      // force the load from the network for the new filters
      this.load(newLoadEvent)
    }
  }

  /**
   *
   * Load data into the table.
   *
   * Normally this function is called when the onLazyLoad event is emitted by the underlying (prime) table,
   * on initial load and subsequent sorts / filters.
   */
  load (event: TableLazyLoadEvent) {
    // API is disabled, ignore this request.
    if (this.disableApi) return

    // table is not ready to load yet, ignore this request
    if (!this.filterInited) return

    setTimeout(() => { this.loading = true })

    // we may need to add to our LazyLoadEvent the global search value, which will not be provided by primeng in the load() call…
    // start by clearing the existing value
    delete event.filters!.global_search
    // now determine if we have a search value from the GlobalSearch service, and if so append it to our incoming event
    const globalSearchValue = this.globalSearchText
    if (globalSearchValue !== '') {
      event.filters!.global_search = ([{
        value: globalSearchValue,
        matchMode: 'equals',
        operator: 'and',
      }]) as FilterMetadata
    }

    // store the filters to potentially use for a reload
    this.lastLoadEvent = cloneDeep(event) // Use `cloneDeep` instead of `structuredClone` because TableLazyLoadEvent.forceUpdate is a function.

    // in case the user updated the filters directly on the table, or when the table loads from the url,
    // update the current filters, sort and pagination in the filters service / widget
    const payload = this.configureFiltersService.lazyLoadEventToFilterServicePayload(event, this.uniqueKey)
    this.configureFiltersService.setFilters(this.uniqueKey, payload)

    // potentially reset the url to reflect our current sort / filter / pagination config
    if (this.updateUrlOnLoad) {
      this.configureFiltersService.resetURL(this.uniqueKey)
    }

    // get the current filter / sort config for the re/load
    let urlParams = lazyLoadEventToUrlParams(event)
    // apply any compulsory filters we may have
    urlParams = Object.assign(urlParams, this.forceFilters)

    this.api[this.apiLoadMethod](urlParams).subscribe(
      (response: PagedResponse<T>) => {
        setTimeout(() => {
          this.tableData = response.results
          this.totalRecords = response.count
          this.checkScreenSize()
          this.dataChange.emit(response)
        })
      },
      (error: any) => {
        console.log('error doing list ! :O')
        console.error(error)
        // re-throw the error, which will be caught by the generic (global) error handler
        throw error
      },
    ).add(() => {
      // either after success or fail set loading to false
      setTimeout(() => {
        this.loading = false
        this.dataLoaded.emit()
      })
    })
  }

  matchModeOptions (primeng: PrimeNG, type: string): SelectItem[] {
    const matchModes = (primeng.filterMatchModeOptions as any)[type].map((key: string) => {
      return { label: primeng.getTranslation(key), value: key }
    })
    matchModes.splice(1, 0, { label: $localize`:@@prime-notContains:Not contains`, value: 'notContains' })
    matchModes.push({ label: $localize`:@@prime-exists:Exists`, value: 'exists' })
    return matchModes
  }

  /**
   * Multi-select group item click handler.
   * Check or uncheck all the children of this group item.
   */
  multiSelectGroupSelectChanged (checkedGroups: string[], currentGroup: SelectItemGroup, selectedItemValues: (string|number)[] | undefined, filterCallback: CallableFunction) {
    // determine if we are checking or unchecking items
    let selectState = false
    if (checkedGroups.includes(currentGroup.value)) {
      selectState = true
    }

    // loop over all the currently selected items in this group
    currentGroup.items.forEach((item: SelectItem) => {
      if (!selectedItemValues) selectedItemValues = []
      // The SelectItem value may either be a code directly, or an object containing the code.
      // Here we look for an attribute called `code` and if it is present use it otherwise fall back to the full object.
      const itemKey: string | number = item.value.code ? item.value.code : item.value

      // Start by deselecting all items
      remove(selectedItemValues, (itemValue: string | number) => itemValue === itemKey)
      // is this is a 'select' operation, select all items in the group
      if (selectState) {
        selectedItemValues.push(itemKey)
      }
    })

    // call the base column filter callback to apply the new filter
    filterCallback(selectedItemValues)
  }

  onModelChange (filterConstraint: FilterMetadata, type: string, value: any) {
    filterConstraint.value = value

    if (type === 'boolean' || value === '') {
      this.pTable._filter()
    }
  }

  /**
   * Change the current filters and call for the table to update
   */
  changeActiveFilters (filterPayload: FilterServicePayload) {
    this.clearFilters(false, false)

    const map = new Map<string, any>()
    filterPayload.filters.forEach(filter => {
      if (!filter.columnComponent) return
      const result = buildFilterKeyValue(filter, filter.columnComponent.filterKey || filter.columnComponent.key)
      map.set(result[0], result[1])
    })

    // build multi-field sort
    const orderFields: string[] = []
    filterPayload.multiSortMeta?.forEach(sortData => {
      let sortField = sortData.field
      if (sortData.order === -1) {
        sortField = '-' + sortField
      }
      orderFields.push(sortField)
    })
    map.set('ordering', orderFields.join(','))
    map.set('limit', filterPayload.rows)
    map.set('offset', filterPayload.first)
    // call for the table reset from the url params map
    this.resetTableStateFromMap(map)
  }

  onTextInputEnterKeyDown (_filterConstraint: FilterMetadata, event: Event) {
    this.pTable._filter()
    event.preventDefault()
  }

  onNumericInputKeyDown (event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.pTable._filter()
      event.preventDefault()
    }
  }

  /**
   * Compute the style for a given column.
   * This:
   * - sets the min-width of the column;
   * - hides mobile column on non-mobile view;
   * - hides non-mobile column on mobile view.
   */
  computeColumnStyle (column: GenericColumnComponent): string {
    const values = []
    values.push('min-width: ' + column.minWidth)
    if ((this.showMobileView && !column.isMobileColumn) || (!this.showMobileView && column.isMobileColumn)) {
      values.push('display: none')
    }
    return values.join('; ')
  }
}
